/**
 * Copyright (c) 2020-present, NDLA.
 *
 * This source code is licensed under the GPLv3 license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { gql, type TypedDocumentNode } from "@apollo/client";
import { useSuspenseQuery } from "@apollo/client/react";
import { Badge, CardContent, CardHeading, CardImage, CardRoot, Heading, Text } from "@ndla/primitives";
import { SafeLink } from "@ndla/safelink";
import { styled } from "@ndla/styled-system/jsx";
import { linkOverlay } from "@ndla/styled-system/patterns";
import type { ImageVariantDTO } from "@ndla/types-backend/image-api";
import { BadgesContainer } from "@ndla/ui";
import parse from "html-react-parser";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { DefaultErrorMessagePage } from "../../components/DefaultErrorMessage";
import { PageContainer } from "../../components/Layout/PageContainer";
import { NavigationBox } from "../../components/NavigationBox";
import { PageTitle } from "../../components/PageTitle";
import { SKIP_TO_CONTENT_ID } from "../../constants";
import type {
  GQLMovedResourcePage_NodeFragment,
  GQLMovedResourceQuery,
  GQLMovedResourceQueryVariables,
} from "../../graphqlTypes";
import { useListItemTraits } from "../../util/listItemTraits";

interface Props {
  resource: GQLMovedResourcePage_NodeFragment;
}

const StyledMain = styled("main", {
  base: {
    display: "flex",
    gap: "xxlarge",
    flexDirection: "column",
  },
});

const StyledHeading = styled(Heading, {
  base: {
    textAlign: "center",
  },
});

const StyledText = styled(Text, {
  base: {
    textAlign: "center",
  },
});

const StyledCardRoot = styled(CardRoot, {
  base: {
    alignSelf: "center",
    height: "full",
    width: "full",
    maxWidth: "[360px]",
  },
});

const movedResourceQuery: TypedDocumentNode<GQLMovedResourceQuery, GQLMovedResourceQueryVariables> = gql`
  query movedResource($resourceId: String!) {
    resource: node(id: $resourceId) {
      contexts {
        contextId
        url
        breadcrumbs
      }
    }
  }
`;

export const MovedResourcePage = ({ resource }: Props) => (
  <Suspense fallback={null}>
    <MovedResourcePageContent resource={resource} />
  </Suspense>
);

const MovedResourcePageContent = ({ resource }: Props) => {
  const { t } = useTranslation();

  const { error, data } = useSuspenseQuery(movedResourceQuery, {
    variables: { resourceId: resource.id },
  });

  const isTopic = resource.nodeType === "TOPIC";

  const traits = useListItemTraits({
    resourceTypes: resource.resourceTypes,
    traits: resource.article?.traits,
    resourceType: isTopic ? "topic" : undefined,
  });

  if (error) {
    return <DefaultErrorMessagePage />;
  }

  const resourceId = resource.learningpath?.id ?? resource.article?.id;
  const ingress = resource.learningpath?.description ?? resource.article?.metaDescription ?? "";
  const image = resource.learningpath?.coverphoto ?? resource.article?.metaImage;

  const navigationBoxItems = data?.resource?.contexts.map((ctx) => {
    return { url: ctx.url ?? "", label: ctx.breadcrumbs[0] ?? "" };
  });

  return (
    <PageContainer padding="large">
      <PageTitle title={t("htmlTitles.movedResourcePage")} useLocationForCustomPath={true} />
      <meta name="robots" content="noindex" />
      <StyledMain>
        <StyledHeading id={SKIP_TO_CONTENT_ID} textStyle="heading.medium">
          {resourceId
            ? t(isTopic ? "movedResourcePage.topicTitle" : "movedResourcePage.title")
            : t("searchPage.searchResultListMessages.noResultHeading")}
        </StyledHeading>
        {resourceId ? (
          <StyledCardRoot>
            {!!image && (
              <CardImage
                alt={image.alttext.alttext}
                src={image.image.imageUrl}
                width={image.image.dimensions?.width}
                height={image.image.dimensions?.height}
                variants={image.image.variants as ImageVariantDTO[]}
                sizes="360px"
              />
            )}
            <CardContent>
              {!!traits.length && (
                <BadgesContainer>
                  {traits.map((trait) => (
                    <Badge size="small" key={trait}>
                      {trait}
                    </Badge>
                  ))}
                </BadgesContainer>
              )}
              <CardHeading asChild consumeCss>
                <SafeLink to={resource.url ?? ""} css={linkOverlay.raw()}>
                  {resource.name}
                </SafeLink>
              </CardHeading>
              {!!ingress && <Text>{parse(ingress)}</Text>}
              {!!resource.breadcrumbs && (
                <Text color="text.subtle" textStyle="label.small">
                  {resource.breadcrumbs.join(" › ")}
                </Text>
              )}
            </CardContent>
          </StyledCardRoot>
        ) : (
          <StyledText>{t("searchPage.searchResultListMessages.noResultDescription")}</StyledText>
        )}
        <NavigationBox
          heading={t(isTopic ? "movedResourcePage.openTopicInSubject" : "movedResourcePage.openInSubject")}
          items={navigationBoxItems}
        />
      </StyledMain>
    </PageContainer>
  );
};

MovedResourcePage.fragments = {
  resource: gql`
    fragment MovedResourcePage_Node on Node {
      id
      nodeType
      name
      url
      breadcrumbs
      contexts {
        contextId
        url
        breadcrumbs
      }
      article {
        id
        metaDescription
        traits
        metaImage {
          id
          image {
            imageUrl
            dimensions {
              width
              height
            }
            variants {
              size
              variantUrl
              dimensions {
                width
                height
              }
            }
          }
          alttext {
            alttext
          }
        }
      }
      learningpath {
        id
        description
        coverphoto {
          image {
            imageUrl
            dimensions {
              width
              height
            }
            variants {
              size
              variantUrl
              dimensions {
                width
                height
              }
            }
          }
          alttext {
            alttext
          }
        }
      }
      resourceTypes {
        id
        name
      }
    }
  `,
};
